import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const BASE_URL = process.env.REACT_APP_CHATWOOT_BASE_URL;
const WEBSITE_TOKEN = process.env.REACT_APP_CHATWOOT_WEBSITE_TOKEN;

// Loads the Chatwoot live-chat bubble for admins only - mounted conditionally
// in Layout.js (`{isAdmin() && <ChatwootWidget />}`), not for every role. Also
// no-ops entirely until both env vars are set at build time - see
// REACT_APP_CHATWOOT_BASE_URL / REACT_APP_CHATWOOT_WEBSITE_TOKEN in .env.
const ChatwootWidget = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!BASE_URL || !WEBSITE_TOKEN) return;

    if (window.$chatwoot) {
      // Script already loaded from an earlier admin session in this same tab
      // (SPA logout doesn't reload the page) - just re-show the bubble instead
      // of re-injecting the SDK.
      window.$chatwoot.toggleBubbleVisibility('show');
    } else {
      window.chatwootSettings = { locale: 'es', position: 'right', type: 'standard' };

      const script = document.createElement('script');
      script.src = `${BASE_URL}/packs/js/sdk.js`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.chatwootSDK?.run({ websiteToken: WEBSITE_TOKEN, baseUrl: BASE_URL });
      };
      document.body.appendChild(script);
    }

    // Unmounts when this stops being rendered - i.e. the signed-in user is no
    // longer an admin (role switch or logout without a full page reload in the
    // same tab). Hide the bubble and drop the identified user so nothing about
    // the previous admin leaks to whoever is using the browser next.
    return () => {
      window.$chatwoot?.toggleBubbleVisibility('hide');
      window.$chatwoot?.reset();
    };
  }, []);

  useEffect(() => {
    if (!BASE_URL || !WEBSITE_TOKEN || !user) return;

    const identify = () => {
      window.$chatwoot?.setUser(user.id, { email: user.email, name: user.name });
    };
    window.addEventListener('chatwoot:ready', identify);
    return () => window.removeEventListener('chatwoot:ready', identify);
  }, [user]);

  return null;
};

export default ChatwootWidget;
